import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import apiService from '../services/api';
import { useShellUser } from '../components/AppShell';
import DreamEntries from '../components/DreamEntries';
import DreamStreak from '../components/DreamStreak';
import { FlipCard, formatMemberSince } from './PublicProfile';
import { getFanCardColors } from '../utils/fanCardColors';
import SquadsPanel from '../components/profile/SquadsPanel';
import '../styles/Profile.css';

function badgeSrc(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw, window.location.origin);
    if (parsed.pathname.startsWith('/api/')) {
      return `${apiService.getApiOrigin()}${parsed.pathname}${parsed.search}`;
    }
  } catch (_error) {
    return raw;
  }
  return raw;
}

function sortByArt(list) {
  return [...list].sort((a, b) => Number(!a.image_url) - Number(!b.image_url));
}

function Badge({ badge }) {
  const src = badgeSrc(badge.image_url);
  return (
    <div className={`profile-badge${badge.earned === false ? ' is-locked' : ''}`}>
      <span className="profile-badge-mark">
        {src ? <img src={src} alt="" /> : <span className="profile-badge-empty" />}
      </span>
      <strong>{badge.title}</strong>
      {badge.description ? <span>{badge.description}</span> : null}
    </div>
  );
}

function ProfilePage() {
  const { user } = useShellUser();
  const fanId = String(user?.fan_id || '').trim();
  const [profile, setProfile] = useState(null);
  const [cardPrimary, setCardPrimary] = useState('');
  const [badges, setBadges] = useState([]);
  const [badgesLoaded, setBadgesLoaded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!fanId) return undefined;
    let cancelled = false;
    apiService
      .getPublicProfile(fanId)
      .then(async (data) => {
        if (cancelled) return;
        setProfile(data);
        const schoolId = data?.active_school?.id;
        if (!schoolId) return;
        try {
          const branding = await apiService.getPublicSchoolBranding(schoolId);
          if (cancelled) return;
          setCardPrimary(
            String(
              branding?.primaryColor ||
                branding?.button ||
                branding?.buttonColor ||
                branding?.buttonFillColor ||
                ''
            ).trim()
          );
        } catch (_error) {
          if (!cancelled) setCardPrimary('');
        }
      })
      .catch(() => {
        if (!cancelled) setProfile(null);
      });
    return () => {
      cancelled = true;
    };
  }, [fanId]);

  useEffect(() => {
    let cancelled = false;
    apiService
      .getBadges()
      .then((data) => {
        if (!cancelled) setBadges(Array.isArray(data?.badges) ? data.badges : []);
      })
      .catch(() => {
        if (!cancelled) setBadges([]);
      })
      .finally(() => {
        if (!cancelled) setBadgesLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const card = profile || {
    fan_id: fanId,
    first_name: user?.first_name,
    last_name: user?.last_name,
    date_joined: user?.date_joined,
    lifetime_points: user?.lifetime_points,
    transaction_count: null,
    active_school: user?.active_school,
  };
  const name = `${card.first_name || ''} ${card.last_name || ''}`.trim() || 'Member';
  const memberSince = formatMemberSince(card.date_joined || user?.created_at);
  const crest = card.active_school?.image || '';
  const palette = useMemo(
    () => getFanCardColors(cardPrimary || card.active_school?.primary_color || ''),
    [cardPrimary, card.active_school?.primary_color]
  );
  const cardStyle = useMemo(
    () => ({
      background: `linear-gradient(131.956deg, ${palette.from} 38.927%, ${palette.to} 102.57%)`,
      borderColor: palette.border,
    }),
    [palette]
  );

  const earned = sortByArt(badges.filter((badge) => badge?.earned));
  const shown = showAll ? sortByArt(badges) : earned.slice(0, 3);
  const canViewAll = badges.length > shown.length || showAll;

  return (
    <div className="profile-page">
      <div className="profile-col">
        <div className="profile-card">
          <FlipCard
            flat
            profile={card}
            name={name}
            memberSince={memberSince}
            crest={crest}
            cardStyle={cardStyle}
          />
        </div>
        <DreamStreak />
        <DreamEntries />
        <section className="profile-panel">
          <header className="profile-panel-head">
            <h2>Achievements</h2>
            {canViewAll ? (
              <button type="button" onClick={() => setShowAll((open) => !open)}>
                {showAll ? 'Show less' : 'View all'}
              </button>
            ) : null}
          </header>
          {!badgesLoaded ? <p className="profile-panel-empty">Loading achievements...</p> : null}
          {badgesLoaded && !shown.length ? (
            <p className="profile-panel-empty">No achievements yet</p>
          ) : null}
          {shown.length ? (
            <div className="profile-badges">
              {shown.map((badge) => (
                <Badge key={badge.slug} badge={badge} />
              ))}
            </div>
          ) : null}
        </section>
        <SquadsPanel />
      </div>
    </div>
  );
}

function ProfileGate() {
  if (!apiService.isAuthenticated()) return <Navigate to="/login" replace />;
  return <ProfilePage />;
}

export default ProfileGate;
